/**
 * Connected App Logger.
 *
 * Two explicit methods plus automatic capture of uncaught errors:
 *
 *   app.log.info("User clicked export");
 *   app.log.error("Payment sync failed", err);
 *
 * Auto-capture (enabled by default):
 *   - window 'error' events     (syntax errors, throw in event handler, etc.)
 *   - window 'unhandledrejection' events  (Promise rejections with no .catch)
 *
 * To opt out: app.log.disableAutoCapture()
 *
 * Built-in safety:
 *   - re-entry guard so a failing log emit doesn't recurse into itself
 *   - same-message dedupe within a 1s window
 *   - burst cap of 10 auto-captures per second
 */
export default class Log {
  constructor(client) {
    this.client = client;
    this._autoCapture = true;
    this._emitting = false;
    this._recent = new Map();          // message -> last-seen timestamp
    this._burstTimes = [];              // recent emit timestamps
    this._dedupeWindowMs = 1000;
    this._burstWindowMs = 1000;
    this._burstLimit = 10;

    this._installGlobalHandlers();
  }

  // ── Public API ─────────────────────────────────────────────────────

  /**
   * Push an INFO-level entry.
   * @param {string} message - human-readable summary
   */
  info(message) {
    this.client._postMessage("log.info", {
      level: "INFO",
      message: truncate(toMessage(message), MAX_MESSAGE),
    });
  }

  /**
   * Push an ERROR-level entry.
   * @param {string} message - human-readable summary
   * @param {Error|string} [error] - exception or pre-formatted stack string
   */
  error(message, error) {
    this.client._postMessage("log.error", {
      level: "ERROR",
      message: truncate(toMessage(message), MAX_MESSAGE),
      stack: truncate(toStack(error), MAX_STACK),
    });
  }

  /** Disable automatic capture of uncaught errors / promise rejections. */
  disableAutoCapture() {
    this._autoCapture = false;
  }

  /** Re-enable automatic capture (it's on by default). */
  enableAutoCapture() {
    this._autoCapture = true;
  }

  // ── Internals ──────────────────────────────────────────────────────

  _installGlobalHandlers() {
    if (typeof window === "undefined") return;
    // Only install once even if SDK is re-initialized in the same page.
    if (window.__facilioAppsSdkLogHandlersInstalled) return;
    window.__facilioAppsSdkLogHandlersInstalled = true;

    window.addEventListener("error", (event) => {
      // Resource-load errors fire on the target (img, script…) and bubble to window
      // with event.error === null. Skip those — they're noise without context.
      if (!event || (!event.error && !event.message)) return;
      // Cross-origin script errors arrive as "Script error." with no stack — useless.
      if (!event.error && event.message === "Script error.") return;

      const message =
        event.message ||
        (event.error && event.error.message) ||
        "Uncaught error";

      // Compose a useful stack when only positional info is available
      const stack =
        (event.error && event.error.stack) ||
        `${message} at ${event.filename || "?"}:${event.lineno || "?"}:${event.colno || "?"}`;

      this._autoEmit(message, stack);
    });

    window.addEventListener("unhandledrejection", (event) => {
      const reason = event && event.reason;
      const message =
        (reason && reason.message) ||
        (typeof reason === "string" ? reason : "Unhandled promise rejection");
      const stack = toStack(reason);
      this._autoEmit(message, stack);
    });
  }

  _autoEmit(message, stack) {
    if (!this._autoCapture) return;
    if (this._emitting) return;           // re-entry guard
    if (this._isOverBurst()) return;
    if (this._isDuplicate(message)) return;

    this._emitting = true;
    try {
      // Reuse the same path as the explicit error() call so auto + manual
      // entries look identical on the server.
      this.error(message, stack);
    } catch (e) {
      // swallow — never let logging take down the connected app
    } finally {
      this._emitting = false;
    }
  }

  _isDuplicate(message) {
    const now = Date.now();
    const last = this._recent.get(message);
    if (last && now - last < this._dedupeWindowMs) {
      return true;
    }
    this._recent.set(message, now);
    if (this._recent.size > MAX_RECENT) this._recent.clear();
    return false;
  }

  _isOverBurst() {
    const now = Date.now();
    this._burstTimes = this._burstTimes.filter(
      (t) => now - t < this._burstWindowMs
    );
    if (this._burstTimes.length >= this._burstLimit) return true;
    this._burstTimes.push(now);
    return false;
  }
}

// ── helpers ──────────────────────────────────────────────────────────

const MAX_MESSAGE = 2000;
const MAX_STACK = 8000;
const MAX_RECENT = 200;

function truncate(s, n) {
  if (s == null) return s;
  return s.length > n ? s.slice(0, n) + "…[truncated]" : s;
}

function toMessage(message) {
  if (message == null) return "";
  if (typeof message === "string") return message;
  try {
    return String(message);
  } catch (e) {
    return "";
  }
}

function toStack(error) {
  if (error == null) return null;
  if (typeof error === "string") return error;
  if (error instanceof Error) {
    return error.stack || `${error.name}: ${error.message}`;
  }
  try {
    return JSON.stringify(error);
  } catch (e) {
    return String(error);
  }
}
